import {
  Directive,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { defaultsDeep, forEach, get, isEqual, replace } from 'lodash';
import { merge, startWith, Subject, takeUntil } from 'rxjs';

import { FormValidationService } from './form-validation.service';
import {
  ADDITIONAL_VALIDATION_MESSAGES,
  CORE_VALIDATION_MESSAGES,
  ValidationMessages,
} from './models';

interface Error {
  message: string;
  params: any;
}

@Directive({
  selector: '[locktonAppsFormGroupValidation]',
  exportAs: 'validation',
  standalone: true,
})
export class FormGroupValidationDirective implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;

  @Output() validationMessageChange = new EventEmitter<
    Record<string, string>
  >();

  validationMessages: Record<string, string> = {};

  _destroy = new Subject<void>();

  constructor(
    private validation: FormValidationService,
    @Optional()
    @Inject(CORE_VALIDATION_MESSAGES)
    private coreValidationMessages: ValidationMessages,
    @Optional()
    @Inject(ADDITIONAL_VALIDATION_MESSAGES)
    private additionalValidationMessages: ValidationMessages
  ) {}

  ngOnInit(): void {
    merge(this.formGroup.valueChanges, this.formGroup.statusChanges)
      .pipe(takeUntil(this._destroy), startWith(this.formGroup.value))
      .subscribe(() => {
        const messagesRecord = defaultsDeep(
          this.additionalValidationMessages || {},
          this.coreValidationMessages || {}
        );
        const messages = this.validation.getValidationErrors(
          this.formGroup,
          messagesRecord
        );

        if (!isEqual(this.validationMessages, messages)) {
          this.validationMessages = messages;
          this.validationMessageChange.emit(messages);
        }
      });
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  getMessageForControl(path: string | string[]): string {
    const error: Error = get(this.validationMessages, path);
    return error ? this.interpolateMessage(error) : '';
  }

  private interpolateMessage(error: Error): string {
    let message = error.message;
    forEach(error.params, (value, key) => {
      message = replace(message, `{ ${key} }`, value);
    });

    return message;
  }
}
